import { Box, IconButton, Typography, InputBase } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useSavedDeals } from "../../hooks/useSavedDeals";
import { useAuth } from "../../contexts/AuthContext";
import CustomButton from "../ui/CustomButton";
import {
  DealListRow,
  DealListRowSkeleton,
} from "./dealList/components/DealListRow";
import DealListLoadMoreButton from "./dealList/components/DealListLoadMoreButton";
import {
  DEAL_LIST_BOTTOM_SCROLL_RESERVE,
  DEAL_LIST_SECTION_PAGE_SIZE,
} from "./dealList/constants";
import { groupDealsByExpiryBucket } from "./dealList/utils/calendarDealBuckets";
import { uiNeutrals } from "../../constants/uiNeutrals";

const SECTIONS = [
  { key: "today", label: "EXPIRING TODAY" },
  { key: "tomorrow", label: "EXPIRING TOMORROW" },
  { key: "thisWeek", label: "EXPIRING THIS WEEK" },
  { key: "later", label: "EXPIRING LATER" },
];

const listShellSx = {
  maxWidth: 720,
  mx: "auto",
  px: { xs: 2 },
  pt: { xs: 0.5, md: 0 },
  pb: DEAL_LIST_BOTTOM_SCROLL_RESERVE,
};

const INITIAL_SECTION_LIMITS = {
  today: DEAL_LIST_SECTION_PAGE_SIZE,
  tomorrow: DEAL_LIST_SECTION_PAGE_SIZE,
  thisWeek: DEAL_LIST_SECTION_PAGE_SIZE,
  later: DEAL_LIST_SECTION_PAGE_SIZE,
};

const SavedSearchBar = ({
  searchInput,
  onSearchChange,
  onClearSearch,
  surfaceNeutral,
}) => {
  const showClear = Boolean(searchInput.trim());
  const trailingIconSx = {
    flexShrink: 0,
    p: 0.35,
    color: "#888",
    WebkitTapHighlightColor: "transparent",
  };
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        py: 0.85,
        px: 1.35,
        mb: 1,
        borderRadius: 2,
        bgcolor: surfaceNeutral.surface,
        border: "none",
        boxShadow: "none",
      }}
    >
      <SearchIcon sx={{ color: "#888", fontSize: 20, flexShrink: 0 }} />
      <InputBase
        placeholder="Search saved deals"
        value={searchInput}
        onChange={onSearchChange}
        autoFocus={false}
        inputProps={{
          inputMode: "search",
          autoCapitalize: "none",
          autoCorrect: "off",
          spellCheck: "false",
          "aria-label": "Search saved deals",
        }}
        sx={{
          flex: 1,
          minWidth: 0,
          "& .MuiInputBase-input": {
            fontSize: "16px",
            lineHeight: 1.25,
          },
        }}
      />
      <IconButton
        type="button"
        size="small"
        onClick={onClearSearch}
        aria-label={showClear ? "Clear search" : undefined}
        aria-hidden={!showClear}
        tabIndex={showClear ? 0 : -1}
        sx={{
          ...trailingIconSx,
          visibility: showClear ? "visible" : "hidden",
          pointerEvents: showClear ? "auto" : "none",
        }}
      >
        <CloseIcon sx={{ fontSize: 22 }} />
      </IconButton>
    </Box>
  );
};

const SavedDealsCalendarTab = ({ isActive, onExpandDeal }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const n = theme.palette.neutral || uiNeutrals;
  const [searchInput, setSearchInput] = useState("");
  const [sectionLimits, setSectionLimits] = useState({
    ...INITIAL_SECTION_LIMITS,
  });

  const { deals, loading, error } = useSavedDeals({
    enabled: isActive,
  });

  /** Rows unsaved while drawer is open: keep in list until drawer closes (server/cache still update). */
  const [ghostDealsByShortId, setGhostDealsByShortId] = useState({});
  const dealsRef = useRef(deals);
  dealsRef.current = deals;

  useEffect(() => {
    if (!isActive) {
      setGhostDealsByShortId({});
    }
  }, [isActive]);

  useEffect(() => {
    if (!isActive) return undefined;

    const onSavedChanged = (e) => {
      const sid = e.detail?.unsavedShortId;
      if (sid == null || sid === "") return;
      const key = String(sid);
      const list = dealsRef.current;
      const deal = list.find(
        (d) =>
          (d.shortId != null && String(d.shortId) === key) ||
          String(d._id) === key,
      );
      if (deal) {
        setGhostDealsByShortId((prev) => ({ ...prev, [key]: { ...deal } }));
      }
    };

    window.addEventListener("savedDealsChanged", onSavedChanged);
    return () =>
      window.removeEventListener("savedDealsChanged", onSavedChanged);
  }, [isActive]);

  const displayDeals = useMemo(() => {
    const seen = new Set(
      deals.map((d) => String(d.shortId != null ? d.shortId : d._id)),
    );
    const extras = Object.values(ghostDealsByShortId).filter((d) => {
      const id = d.shortId != null ? String(d.shortId) : String(d._id);
      return id && !seen.has(id);
    });
    return extras.length ? [...deals, ...extras] : deals;
  }, [deals, ghostDealsByShortId]);

  useEffect(() => {
    setGhostDealsByShortId((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const key of Object.keys(next)) {
        if (deals.some((d) => d.shortId != null && String(d.shortId) === key)) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [deals]);

  useEffect(() => {
    setSectionLimits({ ...INITIAL_SECTION_LIMITS });
  }, [searchInput, displayDeals.length]);

  const filteredDeals = useMemo(() => {
    const q = searchInput.trim().toLowerCase();
    if (!q) return displayDeals;
    return displayDeals.filter(
      (d) =>
        (d.title && String(d.title).toLowerCase().includes(q)) ||
        (d.brand?.name && String(d.brand.name).toLowerCase().includes(q)),
    );
  }, [displayDeals, searchInput]);

  const buckets = useMemo(
    () => groupDealsByExpiryBucket(filteredDeals),
    [filteredDeals],
  );

  const totalInBuckets =
    buckets.today.length +
    buckets.tomorrow.length +
    buckets.thisWeek.length +
    buckets.later.length;

  const searchBar = (
    <SavedSearchBar
      searchInput={searchInput}
      onSearchChange={(e) => setSearchInput(e.target.value)}
      onClearSearch={() => setSearchInput("")}
      surfaceNeutral={n}
    />
  );

  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          ...listShellSx,
          textAlign: "center",
          pt: { xs: 2, md: 1 },
        }}
      >
        <Box sx={{ py: 6 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Login to see saved deals
          </Typography>

          <CustomButton
            variant="secondary"
            onClick={() =>
              navigate("/login", {
                state: {
                  redirectTo: `${location.pathname}${location.search}`,
                },
              })
            }
            sx={{ minWidth: 120, mt: 2 }}
          >
            Login
          </CustomButton>
        </Box>
      </Box>
    );
  }

  if (isActive && loading) {
    return (
      <Box sx={listShellSx}>
        {searchBar}
        {Array.from({ length: 8 }).map((_, i) => (
          <DealListRowSkeleton key={i} />
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={listShellSx}>
        {searchBar}
        <Typography sx={{ color: "#666", fontSize: "0.9rem" }}>
          Could not load saved deals. Try again later.
        </Typography>
      </Box>
    );
  }

  if (searchInput.trim() && filteredDeals.length === 0) {
    return (
      <Box sx={listShellSx}>
        {searchBar}
        <Typography sx={{ color: "#666", fontSize: "0.9rem" }}>
          No saved deals match your search.
        </Typography>
      </Box>
    );
  }

  if (isActive && !loading && displayDeals.length > 0 && totalInBuckets === 0) {
    return (
      <Box sx={listShellSx}>
        {searchBar}
        <Typography sx={{ color: "#666", fontSize: "0.9rem", lineHeight: 1.5 }}>
          No saved deals with a clear end date in this list.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={listShellSx}>
      {searchBar}
      <Box sx={{ mt: 2 }}>
        {SECTIONS.map(({ key, label }) => {
          const list = buckets[key];
          if (!list.length) return null;
          const lim = sectionLimits[key];
          const visible = list.slice(0, lim);
          const showSectionLoadMore = list.length > lim;
          return (
            <Box key={key} sx={{ mb: 0.5 }}>
              <Typography
                sx={{
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  color: "#888",
                  mt: key === "today" ? 0 : 2.25,
                  mb: 1,
                }}
              >
                {label}
              </Typography>
              <Box>
                {visible.map((deal, index) => (
                  <DealListRow
                    key={deal._id}
                    deal={deal}
                    index={index}
                    onExpandDeal={onExpandDeal}
                  />
                ))}
              </Box>
              {showSectionLoadMore ? (
                <DealListLoadMoreButton
                  onClick={() =>
                    setSectionLimits((prev) => ({
                      ...prev,
                      [key]: prev[key] + DEAL_LIST_SECTION_PAGE_SIZE,
                    }))
                  }
                />
              ) : null}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default SavedDealsCalendarTab;
